// components/CustomerListModal.jsx — B1 (2026-10) · قائمة عملاء موحّدة.
//
// يفتحها أي رقم عميل قابل للضغط في الواجهة (شارات الفروع · مفقودون ·
// أول زيارة · زوار فريدون · الخريطة الزمنية للشرائح · دليل العملاء) —
// نفس المكوّن، نفس المرشحات، نفس المصدر الذي حسب الرقم المضغوط (ضمان
// التطابق). يُرسَم عبر Portal (طبقة "modal") فوق كل المحتوى — لا z-index
// حرفي جديد.
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { getSegmentMeta } from "../lib/segmentation";
import { Portal } from "./Portal";
import { CustomerCardModal } from "../inbox/CustomerCard";
import { RecipientMessages, TeamBadge } from "./RecipientMessages";
import { ksaToday } from "../lib/ksaTime";

const fmt = (n) => Number(n || 0).toLocaleString("ar");
const fmtSar = (n) => Number(n || 0).toLocaleString("ar", { maximumFractionDigits: 0 });
// بعض الفروع في هذا النظام معرّفها "" (بدون اسم) — قيمة فعلية، لا "غائبة".
// branchId||undefined كان يُسقطها فيُعامَل الفرع كـ"كل الفروع" خطأً.
const orUndef = (v) => (v === null || v === undefined ? undefined : v);

function relAr(iso) {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "—";
  const d = Math.floor((Date.now() - t) / 86400000);
  if (d <= 0) return "اليوم";
  if (d === 1) return "أمس";
  if (d < 30) return `قبل ${d} يوم`;
  return `قبل ${Math.floor(d / 30)} شهر`;
}

async function fetchAllPhones({ kind, branchId, segmentCode, dateFrom, dateTo, windowDays, membership, winStart, winEnd, card }) {
  const { data } = await api.get("/customers/unified-list/phones", {
    params: {
      kind, branch_id: orUndef(branchId), segment_code: segmentCode || undefined,
      from: dateFrom || undefined, to: dateTo || undefined, window_days: windowDays || undefined,
      membership: membership || undefined, exclude_optout: false,
      win_start: winStart || undefined, win_end: winEnd || undefined, card: card || undefined,
    },
  });
  return data;
}

// B2 (2026-10) — التنقّل الصحيح الوحيد بعيداً عن POSPage: CustomEvent
// "tw-navigate" يغيّر tab في App.js فتُفكَّك POSPage تلقائياً (tab==="pos"
// يصبح false) — بلا hash، بلا reload (كانا يُعيدان tw_tab من localStorage
// فيرجع المستخدم لنفس شاشة المبيعات دائماً، وهذا كان سبب العطل في B1).
function navigateTo(tab) {
  window.dispatchEvent(new CustomEvent("tw-navigate", { detail: { tab } }));
}

// ── مسودة حملة حقيقية على الخادم (بجمهور = نفس الفلتر، بلا حدّ) ────────
async function createDraftCampaign({ kind, branchId, segmentCode, dateFrom, dateTo, windowDays, membership, name, winStart, winEnd, card }) {
  const { data } = await api.post("/customers/unified-list/draft-campaign", {
    kind, branch_id: branchId, segment_code: segmentCode,
    from: dateFrom, to: dateTo, window_days: windowDays, membership, name,
    win_start: winStart || undefined, win_end: winEnd || undefined, card: card || undefined,
  });
  return data;
}

function todayDDMMYYYY() {
  const [y, m, d] = ksaToday().split("-");
  return `${d}-${m}-${y}`;
}

export default function CustomerListModal({
  kind, branchId = null, segmentCode = null, dateFrom = null, dateTo = null,
  windowDays = null, membership = "lifetime", title = "قائمة العملاء", onClose, headerExtra = null,
  winStart = null, winEnd = null, card = null,
}) {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("last_visit");
  const [sortDir, setSortDir] = useState("desc");
  const [state, setState] = useState({ loading: true, err: null, data: null });
  const [busyCopy, setBusyCopy] = useState(false);
  const [busyExport, setBusyExport] = useState(false);
  const [busySend, setBusySend] = useState(false);
  const [profilePhoneKey, setProfilePhoneKey] = useState(null);

  const params = useMemo(() => ({
    kind, branch_id: orUndef(branchId), segment_code: segmentCode || undefined,
    from: dateFrom || undefined, to: dateTo || undefined, window_days: windowDays || undefined,
    membership: membership || undefined, win_start: winStart || undefined, win_end: winEnd || undefined, card: card || undefined,
    page, page_size: pageSize, search: search || undefined, sort_by: sortBy, sort_dir: sortDir,
  }), [kind, branchId, segmentCode, dateFrom, dateTo, windowDays, membership, winStart, winEnd, card, page, pageSize, search, sortBy, sortDir]);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, err: null }));
    api.get("/customers/unified-list", { params })
      .then((r) => { if (!cancelled) setState({ loading: false, err: null, data: r.data }); })
      .catch((e) => { if (!cancelled) setState({ loading: false, err: e?.response?.data?.detail || e.message, data: null }); });
    return () => { cancelled = true; };
  }, [params]);

  const data = state.data;
  const items = data?.items || [];
  const total = data?.total || 0;
  const excludedOptout = data?.excluded_optout_count || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const toggleSort = (col) => {
    if (sortBy === col) { setSortDir((d) => (d === "asc" ? "desc" : "asc")); }
    else { setSortBy(col); setSortDir("desc"); }
    setPage(1);
  };

  const onCopy = async () => {
    setBusyCopy(true);
    try {
      const res = await fetchAllPhones({ kind, branchId, segmentCode, dateFrom, dateTo, windowDays, membership, winStart, winEnd, card });
      const text = (res.phones || []).join("\n");
      await navigator.clipboard.writeText(text);
      toast.success(`نُسخ ${fmt(res.phones.length)} رقم`);
    } catch (e) {
      toast.error("تعذّر نسخ الأرقام", { description: e?.response?.data?.detail || e.message });
    } finally { setBusyCopy(false); }
  };

  const onExport = async () => {
    setBusyExport(true);
    try {
      const resp = await api.get("/customers/unified-list/export", {
        params: {
          kind, branch_id: orUndef(branchId), segment_code: segmentCode || undefined,
          from: dateFrom || undefined, to: dateTo || undefined, window_days: windowDays || undefined,
          win_start: winStart || undefined, win_end: winEnd || undefined, card: card || undefined,
          membership: membership || undefined,
        },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `customers_${segmentCode || kind}_${branchId || "all"}_${today}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("تم تصدير Excel");
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("تحتاج صلاحية «تصدير القائمة الموحّدة» — اطلبها من المالك");
      } else {
        toast.error("تعذّر التصدير", { description: e?.response?.data?.detail || e.message });
      }
    } finally { setBusyExport(false); }
  };

  const onBulkSend = async () => {
    setBusySend(true);
    try {
      const draftName = `${title} — ${todayDDMMYYYY()}`;
      const res = await createDraftCampaign({
        kind, branchId, segmentCode, dateFrom, dateTo, windowDays, membership, winStart, winEnd, card,
        name: draftName,
      });
      const audienceCount = res.final_eligible_count ?? res.audience_count ?? 0;
      const invalidCount = res.excluded_invalid_count || 0;
      const dupCount = res.excluded_duplicate_count || 0;
      const exclCount = res.excluded_optout_count || 0;
      const rule6Count = res.excluded_rule6_count || 0;
      const parts = [`${fmt(audienceCount)} عميل`];
      const exclParts = [];
      if (invalidCount) exclParts.push(`${fmt(invalidCount)} غير صالح`);
      if (dupCount) exclParts.push(`${fmt(dupCount)} مكرَّر`);
      if (exclCount) exclParts.push(`${fmt(exclCount)} من قائمة الاستبعاد`);
      if (rule6Count) exclParts.push(`${fmt(rule6Count)} وصلته رسالة خلال 7 أيام`);
      const exclText = exclParts.length ? ` (بعد استبعاد ${exclParts.join("، ")})` : "";
      toast.success(
        `تم إنشاء المسودة: ${res.name} · ${parts[0]}${exclText}`,
        {
          duration: 12000,
          action: {
            label: "افتح المسودة",
            onClick: () => { onClose?.(); navigateTo("camp_sched"); },
          },
        }
      );
    } catch (e) {
      toast.error("تعذّر إنشاء المسودة", { description: e?.response?.data?.detail || e.message });
    } finally { setBusySend(false); }
  };

  const SortTh = ({ col, children, align = "center" }) => (
    <th
      onClick={() => toggleSort(col)}
      data-testid={`clm-sort-${col}`}
      style={{ padding: "10px 12px", textAlign: align, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
    >
      {children} {sortBy === col ? (sortDir === "asc" ? "▲" : "▼") : ""}
    </th>
  );

  return (
    <>
    <Portal level="modal">
      <div
        dir="rtl"
        data-testid="customer-list-modal-backdrop"
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, background: "rgba(61,42,28,0.7)",
          backdropFilter: "blur(4px)", display: "flex", alignItems: "center",
          justifyContent: "center", padding: 16,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          data-testid="customer-list-modal"
          style={{
            background: "#FFFFFF", borderRadius: 20, maxWidth: 1040, width: "100%",
            maxHeight: "88vh", overflowY: "auto", padding: "24px 22px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)", color: "#3D2A1C",
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Noto Naskh Arabic', sans-serif",
          }}
        >
          <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, gap: 10, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800 }} data-testid="clm-title">{title}</div>
              {headerExtra}
              <div style={{ fontSize: 12, color: "#78716C", marginTop: 3 }} data-testid="clm-total-count">
                {state.loading ? "جارٍ التحميل…" : `${fmt(total)} عميل${excludedOptout ? ` · ${fmt(excludedOptout)} مستبعَد من واتساب` : ""}`}
              </div>
            </div>
            <button type="button" onClick={onClose} data-testid="clm-close" style={{
              border: "none", background: "rgba(0,0,0,0.06)", color: "#57534E",
              width: 34, height: 34, borderRadius: 10, fontSize: 17, cursor: "pointer",
            }}>✕</button>
          </header>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
            <input
              data-testid="clm-search"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="ابحث بالاسم أو الجوال…"
              style={{ flex: "1 1 220px", padding: "8px 12px", borderRadius: 10, border: "1px solid rgba(61,42,28,0.2)", fontSize: 13 }}
            />
            <button type="button" data-testid="clm-copy-btn" onClick={onCopy} disabled={busyCopy}
              style={{ padding: "8px 14px", borderRadius: 10, border: "1px solid rgba(61,42,28,0.2)", background: "#FFF", cursor: "pointer", fontSize: 12.5, fontWeight: 700 }}>
              {busyCopy ? "جارٍ النسخ…" : "📋 نسخ الأرقام"}
            </button>
            <button type="button" data-testid="clm-export-btn" onClick={onExport} disabled={busyExport}
              style={{ padding: "8px 14px", borderRadius: 10, border: "1px solid rgba(61,42,28,0.2)", background: "#FFF", cursor: "pointer", fontSize: 12.5, fontWeight: 700 }}>
              {busyExport ? "جارٍ التصدير…" : "⬇️ تصدير Excel"}
            </button>
            <button type="button" data-testid="clm-bulk-send-btn" onClick={onBulkSend} disabled={busySend}
              style={{ padding: "8px 14px", borderRadius: 10, border: "1px solid #25D366", background: "#FFF", color: "#128C7E", cursor: "pointer", fontSize: 12.5, fontWeight: 700 }}>
              {busySend ? "جارٍ التجهيز…" : "📨 إرسال جماعي (مسودة)"}
            </button>
          </div>

          {state.loading && (
            <div data-testid="clm-loading" style={{ padding: 40, textAlign: "center", color: "#78716C" }}>جارٍ التحميل…</div>
          )}
          {state.err && !state.loading && (
            <div data-testid="clm-error" style={{ padding: 16, borderRadius: 12, background: "#FEF3F2", border: "1px solid rgba(185,28,28,0.35)", color: "#B91C1C", fontSize: 13 }}>
              تعذّر تحميل القائمة: {state.err}
            </div>
          )}
          {!state.loading && !state.err && items.length === 0 && (
            <div data-testid="clm-empty" style={{ padding: 40, textAlign: "center", color: "#78716C" }}>لا يوجد عملاء مطابقون.</div>
          )}

          {!state.loading && !state.err && items.length > 0 && (
            <>
              <div style={{ border: "1px solid rgba(61,42,28,0.12)", borderRadius: 14, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }} data-testid="clm-table">
                  <thead>
                    <tr style={{ background: "#F7F2EC", color: "#78716C" }}>
                      <SortTh col="name" align="right">الاسم</SortTh>
                      <th style={{ padding: "10px 12px", textAlign: "right" }}>الجوال</th>
                      <th style={{ padding: "10px 12px", textAlign: "center" }}>الشريحة</th>
                      <SortTh col="visits">زيارات العمر</SortTh>
                      <SortTh col="spent">إنفاق العمر</SortTh>
                      <SortTh col="last_visit">آخر زيارة</SortTh>
                      <th style={{ padding: "10px 12px", textAlign: "center" }}>الفرع الأكثر زيارة</th>
                      <th style={{ padding: "10px 12px", textAlign: "center" }}>واتساب</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((c, i) => (
                      <tr key={c.phone_key || i} data-testid={`clm-row-${i}`}
                          style={{ borderTop: "1px solid rgba(61,42,28,0.08)", background: i % 2 ? "#FFFFFF" : "#FEFCF9" }}>
                        <td style={{ padding: "10px 12px", fontWeight: 600 }}>
                          {c.phone_key ? (
                            <button type="button" data-testid={`clm-open-profile-${i}`}
                              onClick={() => setProfilePhoneKey(c.phone_key)}
                              style={{ background: "none", border: "none", padding: 0,
                                       color: "#92400E", fontWeight: 700, cursor: "pointer",
                                       textDecoration: "underline" }}>
                              {c.name || "—"}
                            </button>
                          ) : (c.name || "—")}
                          {c.is_team && <TeamBadge testId={`clm-team-badge-${i}`} />}
                          <RecipientMessages messages={c.messages} idx={i} />
                        </td>
                        <td style={{ padding: "10px 12px", direction: "ltr", textAlign: "right", fontFamily: "monospace", color: "#57534E" }}>{c.phone_display || c.phone}</td>
                        <td style={{ padding: "10px 12px", textAlign: "center" }}>{c.segment ? getSegmentMeta(c.segment).label : "—"}</td>
                        <td style={{ padding: "10px 12px", textAlign: "center" }}>{fmt(c.visits)}</td>
                        <td style={{ padding: "10px 12px", textAlign: "center" }}>{fmtSar(c.spent)} ر</td>
                        <td style={{ padding: "10px 12px", textAlign: "center" }}>{relAr(c.last_visit)}</td>
                        <td style={{ padding: "10px 12px", textAlign: "center" }}>{c.top_branch_name || "—"}</td>
                        <td style={{ padding: "10px 12px", textAlign: "center" }}>
                          {c.wa_optout
                            ? <span data-testid={`clm-optout-${i}`} style={{ color: "#B91C1C", fontWeight: 700, fontSize: 11 }}>🚫 مستبعَد</span>
                            : <span style={{ color: "#16A34A", fontSize: 11 }}>✓ متاح</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12, fontSize: 12.5, color: "#57534E" }}>
                <span data-testid="clm-page-info">صفحة {fmt(page)} من {fmt(totalPages)}</span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button type="button" data-testid="clm-prev-page" disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(61,42,28,0.2)", background: "#FFF", cursor: page <= 1 ? "not-allowed" : "pointer", opacity: page <= 1 ? 0.5 : 1 }}>
                    السابق
                  </button>
                  <button type="button" data-testid="clm-next-page" disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(61,42,28,0.2)", background: "#FFF", cursor: page >= totalPages ? "not-allowed" : "pointer", opacity: page >= totalPages ? 0.5 : 1 }}>
                    التالي
                  </button>
                </div>
              </div>
            </>
          )}

          <div style={{ marginTop: 14, padding: "10px 14px", borderRadius: 10, background: "#FEF3C7", border: "1px solid #FBBF24", color: "#78350F", fontSize: 11, lineHeight: 1.6 }}>
            🔒 «إرسال جماعي» ينشئ مسودة حملة حقيقية فوراً (جمهور = هذه القائمة بعد استبعاد واتساب) وتظهر في «الحملات المجدولة» — الإطلاق معطّل حتى يختار المالك قالباً معتمَداً ويفعّله يدوياً من هناك. لا رسائل تُرسَل من هنا.
          </div>
        </div>
      </div>
    </Portal>
    <CustomerCardModal client={null} phone={profilePhoneKey}
      open={!!profilePhoneKey} onClose={() => setProfilePhoneKey(null)} />
    </>
  );
}
